import { COMMA, ENTER, T } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Feature, FeatureCollection, Geometry, Point } from 'geojson';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import embed, { Mode, VisualizationSpec } from 'vega-embed';
import { ConfigGraficoChuvas } from '../models/config-grafico-chuvas';
import { EstacaoTempoChuva, GrupoChuvaEstacao } from '../models/grupo-chuva-estacao';
import { TimeResolution } from '../models/time-comparator/time-resolution';
import { HistoryService } from '../services/history/history.service';
import { RainChartService } from '../services/rain-chart/rain-chart.service';
import { Status } from '../models/status';

/**
 * Gráfico de linhas de chuva. O usuário pode escolher algumas opções de
 * acumulado de chuva usado na construção do gráfico.
 */
@Component({
  selector: 'app-rain-chart',
  templateUrl: './rain-chart.component.html',
  styleUrls: ['./rain-chart.component.css']
})
export class RainChartComponent implements OnInit {

  status = Status.SUCCESS;
  statusEnum = Status;
  timeResolution: TimeResolution = TimeResolution.ONE_HOUR;
  selectedStations: string[] = [];
  filteredStations: Observable<string[]>;
  allStations: string[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];

  /** Indica se o componente está fechado (escondido) no momento */
  private isExpansionPanelOpen: boolean = false;

  isDragDisabled: boolean = true;
  toggleSelect = false;

  timeResolutionChartData: {[key: string]: EstacaoTempoChuva[]} = {};
  /**
  * Textos do tooltips do rainchartbar.
  */
  rainChartTooltipText:string = `Chuva ao longo do tempo dentro de um intervalo.
    Exibe a média da cidade, e é possível filtrar estações. Permite selecionar 
    a resolução de tempo do acumulado a ser exibido no gráfico.`;

  @Output() dragDisabled = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<boolean>();
  @Output() stationToggled = new EventEmitter<string>();
  @Input() startDate: string;
  @Input() endDate: string;

  @ViewChild('stationInput') stationInput: ElementRef<HTMLInputElement>;
  stationCtrl: FormControl = new FormControl();

  constructor(private rainChartSrv: RainChartService, private historyService: HistoryService) {
    this.filteredStations = this.stationCtrl.valueChanges.pipe(
      startWith(null),
      map((station: string | null) => this._filter(station))
    )
  }

  ngOnInit(): void {
  }

  /**
   * Define o status de carregamento do gráfico
   * @param status 
   */
  setStatus(status: Status) {
    this.status = status;
  }

  /**
   * Inicializa o estado do gráfico. Só é chamado quando um novo intervalo de tempo é pesquisado
   * @param config parâmetros de configuração do gráfico.
   */
  initialize(config: ConfigGraficoChuvas) {

    let { estacoesTemposChuvas, resolucaoTempo, dataInicial, dataFinal } = config;

    this.setStatus(Status.SUCCESS);
    this.clearTimeResolutionChartData();
    this.setTimeResolution(resolucaoTempo);
    this.setChartData(estacoesTemposChuvas);
    this.setStartDate(dataInicial);
    this.setEndDate(dataFinal);
  }

  /**
   * Define os pontos do gráfico na resolução de tempo corrente.
   * @param groups dados das estações com os respectivos tempos das medições de chuva e os acumulados medidos.
   *    Cada elemento representa uma estação com um array de objetos que possuem os tempos e acumulados.
   *    Para que esses dados de estações sejam usados no gráfico, é necessário fazer um unflatten.
   */
  setChartData(groups: GrupoChuvaEstacao[]) {
    const arr : EstacaoTempoChuva[] = [];
    groups.forEach(group => {
      group.tempos.forEach(tempo => {
        let item : EstacaoTempoChuva = { ...tempo, 
          'nome_estacao_original': group.nome_estacao_original };
        
        // lida com acumulados de chuva iguais a null (que significa ausência de registro de chuva).
        item["media"] = item["media"] == null ? 0 : +item["media"];
        arr.push(item);
      })
    });

    this.timeResolutionChartData[this.timeResolution] = arr;

    this.initStationsSelect();
  }

  /**
   * Define a resolução de tempo atual exibida pelo gráfico
   * @param timeResolution resolução de tempo selecionada.
   */
  setTimeResolution(timeResolution: TimeResolution) {
    this.timeResolution = timeResolution;
  }

  /**
   * Define a data de início do gráfico (eixo horizontal).
   * @param dateTime data de início no formato YYYY-MM-dd hh:mm
   */
  setStartDate(dateTime: string) {
    this.startDate = dateTime;
  }

  /**
   * Define a data de término do gráfico (eixo horizontal).
   * @param dateTime data de término no formato YYYY-MM-dd hh:mm
   */
  setEndDate(dateTime: string) {
    this.endDate = dateTime;
  }

  /**
   * Atualiza o gráfico, exibindo as estações selecionadas no intervalo de datas selecionado pelo usuário.
   */
  async updateChart() {

    this.rainChartSrv.initialize({
      chartData: this.timeResolutionChartData[this.timeResolution],
      timeResolution: this.timeResolution,
      selectedStations: this.selectedStations,
    });
    let spec : VisualizationSpec = this.rainChartSrv.createChartSpec();

    const embedOpts = {
      actions: {
        "export": true,
        "source": false,
        "compiled": false,
        "editor": false
      },
      mode: 'vega-lite' as Mode,
      defaultStyle: true,
      i18n: {
        PNG_ACTION: "Salvar como PNG",
        SVG_ACTION: "Salvar como SVG",
      },
    };

    await embed('#plotting-area', spec, embedOpts);

  }

  /**
   * Inicializa as opções do select dos nomes das estações.
   * 
   * @returns 
   */
  initStationsSelect() {

    let stationsNames = new Set(
      this.timeResolutionChartData[this.timeResolution].map(feature => feature.nome_estacao_original)
    );

    this.allStations = [...stationsNames];
  }

  /**
   * 
   * @param event 
   */
  headerMouseDown(event: any) {
    this.dragDisabled.emit(false)
  }

  /**
   * 
   * @param event 
   */
  headerMouseUp(event: any) {
    this.dragDisabled.emit(true);
  }

  /**
   * Sinaliza o fechamento do componente do gráfico
   */
  closeChart() {
    this.closeExpansionPanel();
    this.close.emit();
  }

  /** 
   * Fecha o expansion panel do accordion. Isso é importante
   * para evitar que o expansion panel fique "por cima" do mapa
   * quando o componente do gráfico for fechado com visibility hidden.
   */
  closeExpansionPanel() {
    this.isExpansionPanelOpen = false;
  }

  /**
   * Adiciona um termo pesquisado se ele existir no array de opções disponíveis.
   * A comparação do termo com as opções não diferenia letras maiúsculas de minúsculas.
   * 
   * @param event 
   */
  async add(event: MatChipInputEvent) {

    const value = (event.value || '').trim();
    if (value) {
      const valUpper = value.toUpperCase();
      const index = this.allStations.findIndex(station => station.toUpperCase() == valUpper);
      if (index > -1) {
        this.selectedStations.push(this.allStations[index]);
        this.stationToggled.emit(this.allStations[index]);
      }
    }
    event.chipInput!.clear();
    this.stationCtrl.setValue(null);

    await this.updateChart();
  }

  /**
   * Adiciona uma opção selecionada no auto complete ao array de estações selecionadas.
   * 
   * @param event 
   */
  async selected(event: MatAutocompleteSelectedEvent) {
    this.selectedStations.push(event.option.viewValue);
    this.stationInput.nativeElement.value = '';
    this.stationCtrl.setValue(null);
    this.stationToggled.emit(event.option.viewValue);

    await this.updateChart();
  }

  /**
   * Remove uma estação do array de estações selecionadas.
   * 
   * @param station nome da estação a ser removida
   */
  async remove(station: string) {
    
    const index = this.selectedStations.indexOf(station);

    if (index > -1) {
      this.selectedStations.splice(index, 1);
      this.stationToggled.emit(station);
    }

    await this.updateChart();
  }

  /**
   * filtra as estações que contêm o termo de pesquisa em seus nomes.
   * 
   * @param value termo de pesquisa
   * @returns nomes das estações que incluem o termo de pesquisa
   */
  private _filter(value: string | null): string[] {
    const filterVal = value?.toUpperCase();
    
    let notSelected = this.allStations.filter(station => ! this.selectedStations.includes(station));
    return filterVal === null ? 
      notSelected : 
      notSelected.filter(station => station.toUpperCase().includes(filterVal));
  }

  /**
   * Reage a alterações no campo de resoluções do tempo.
   * Se ainda não houver dados na resolução de tempo para o intervalo de tempo atual,
   *    busca esses dados no backend.
   */
  async onTimeResolutionChange() {

    if (this.isTimeResolutionDataSet(this.timeResolution)) {
      await this.updateChart();
      return;
    }

    let interval = this.timeResolution;
    this.status = Status.LOADING;
    this.historyService.getRainDataAggregatesInInterval(this.startDate, this.endDate, interval)
      .subscribe(async (val: GrupoChuvaEstacao[]) => {
        this.status = Status.SUCCESS;
        this.setChartData(val);
        await this.updateChart();
      });
  }

  /**
   * Obtém a string do intervalo de datas para o título do componente
   * @returns intervalo de datas, se estas forem definidas, ou string vazia, caso contrário.
   */
  getDateInterval(): string {
    return this.startDate && this.endDate ? `de ${this.startDate} a ${this.endDate}` : '';
  }

  /**
   * Alterna entre adicionar / remover o nome da estação do array de estações selecionadas.
   * @param stationName nome da estação a ser adicionada / removida
   */
  async toggleSelectedStation(stationName: string) {
    
    const index = this.selectedStations.indexOf(stationName);
    if (index == -1) {
      this.selectedStations.push(stationName);
    }
    else {
      this.selectedStations.splice(index, 1);
    }
    await this.updateChart();
  }

  /**
   * Remove os dados do gráfico em todas as resoluções de tempo
   */
   clearTimeResolutionChartData() {
    for (let timeResolution of Object.keys(this.timeResolutionChartData)) {
      this.timeResolutionChartData[timeResolution] = [];
    }
  }

  /**
   * Verifica se existem dados da resolução de tempo no intervalo de tempo atual
   * @returns true se houver dados, false caso contrário
   */
  isTimeResolutionDataSet(timeResolution: string) : boolean {
    return this.timeResolutionChartData[timeResolution] != null &&
      this.timeResolutionChartData[timeResolution].length > 0;
  }

}
